import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Link from "@mui/material/Link"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"

import CampaignLinksStatus from "@components/campaign_links_status"
import useTranslate from "@hooks/use_translate"
import { type AdminCampaignLinkRow } from "@models/campaign_links"
import { formatDate } from "@utils/functions/formatDate"

export default function CampaignLinksTable({
  rows,
  canEdit,
  onToggle
}: {
  rows: AdminCampaignLinkRow[]
  canEdit: boolean
  onToggle: (row: AdminCampaignLinkRow) => void
}) {
  const { translate, currentLanguage } = useTranslate()

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("campaign_links_no_campaigns")}
      </Typography>
    )
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{translate("campaign_links_campaign")}</TableCell>
          <TableCell>{translate("campaign_links_status")}</TableCell>
          <TableCell>{translate("campaign_links_started_at")}</TableCell>
          <TableCell>{translate("campaign_links_destination")}</TableCell>
          <TableCell align="right">
            {translate("campaign_links_clicks")}
          </TableCell>
          <TableCell>{translate("campaign_links_links")}</TableCell>
          {canEdit && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row._id}>
            <TableCell>
              {row.title || row._id}
              {!!row.deletedAt && (
                <Chip
                  size="small"
                  sx={{ ml: 1 }}
                  label={translate("campaign_links_deleted")}
                />
              )}
            </TableCell>
            <TableCell>
              {[row.status, row.statusReason].filter(Boolean).join(" · ")}
            </TableCell>
            <TableCell>
              {row.startedAt ? formatDate(currentLanguage, row.startedAt) : "—"}
            </TableCell>
            <TableCell sx={{ maxWidth: 280, wordBreak: "break-all" }}>
              {row.destinationUrl ? (
                <Link
                  href={row.destinationUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {row.destinationUrl}
                </Link>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="right">{row.clicked}</TableCell>
            <TableCell>
              <CampaignLinksStatus status={row.links} />
            </TableCell>
            {canEdit && (
              <TableCell align="right">
                <Button
                  size="small"
                  color={row.links.disabled ? "primary" : "error"}
                  onClick={() => onToggle(row)}
                >
                  {translate(
                    row.links.disabled
                      ? "campaign_links_turn_on"
                      : "campaign_links_turn_off"
                  )}
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
